// services/webrtcService.js
class WebRTCService {
    constructor() {
      this.peerConnections = new Map();
      this.localStream = null;
      this.screenStream = null;
      this.configuration = {
        iceServers: [
          { urls: 'stun:stun.l.google.com:19302' },
          { urls: 'stun:stun1.l.google.com:19302' },
          { urls: 'stun:stun2.l.google.com:19302' },
          { urls: 'stun:stun3.l.google.com:19302' },
          { urls: 'stun:stun4.l.google.com:19302' }
        ],
        iceCandidatePoolSize: 10
      };
    }
  
    /**
     * Initialize local media stream with better error handling
     * @param {Object} constraints - Media constraints
     * @returns {Promise<Object>} - { success: boolean, stream?: MediaStream, error?: Error }
     */
    async initializeLocalStream(constraints = { video: true, audio: true }) {
      try {
        console.log('🎥 Initializing local stream with constraints:', constraints);
        
        // Check if getUserMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
          throw new Error('Your browser does not support media devices');
        }
  
        // Stop existing stream if any
        if (this.localStream) {
          console.log('🎥 Stopping existing stream before reinitializing');
          this.stopLocalStream();
        }
  
        // Build proper constraints
        const mediaConstraints = {
          audio: constraints.audio ? {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            sampleRate: 48000
          } : false,
          video: constraints.video ? {
            width: { min: 640, ideal: 1280, max: 1920 },
            height: { min: 480, ideal: 720, max: 1080 },
            frameRate: { min: 15, ideal: 30, max: 60 },
            facingMode: 'user'
          } : false
        };
  
        console.log('🎥 Requesting media with constraints:', mediaConstraints);
  
        this.localStream = await navigator.mediaDevices.getUserMedia(mediaConstraints);
        
        // Log track details
        const videoTracks = this.localStream.getVideoTracks();
        const audioTracks = this.localStream.getAudioTracks();
        
        console.log('✅ Local stream initialized successfully:');
        console.log('  📹 Video tracks:', videoTracks.length);
        if (videoTracks.length > 0) {
          const settings = videoTracks[0].getSettings();
          console.log('  📹 Video settings:', {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            facingMode: settings.facingMode,
            enabled: videoTracks[0].enabled
          });
        }
        console.log('  🎤 Audio tracks:', audioTracks.length);
        if (audioTracks.length > 0) {
          console.log('  🎤 Audio enabled:', audioTracks[0].enabled);
        }
  
        return { 
          success: true, 
          stream: this.localStream,
          hasVideo: videoTracks.length > 0,
          hasAudio: audioTracks.length > 0
        };
      } catch (error) {
        console.error('❌ Error accessing media devices:', error);
        console.error('  Error name:', error.name);
        console.error('  Error message:', error.message);
        
        return { 
          success: false, 
          error: error,
          message: this.getErrorMessage(error)
        };
      }
    }
  
    /**
     * Get user-friendly error message
     * @param {Error} error - Error object
     * @returns {string} - User-friendly error message
     */
    getErrorMessage(error) {
      switch (error.name) {
        case 'NotAllowedError':
        case 'PermissionDeniedError':
          return 'Camera/microphone access denied. Please grant permissions and reload the page.';
        case 'NotFoundError':
        case 'DevicesNotFoundError':
          return 'No camera or microphone found on your device.';
        case 'NotReadableError':
        case 'TrackStartError':
          return 'Camera/microphone is already in use by another application.';
        case 'OverconstrainedError':
          return 'Camera/microphone does not meet the required constraints. Trying with lower quality...';
        case 'TypeError':
          return 'Invalid media constraints.';
        case 'AbortError':
          return 'Media device access was aborted.';
        default:
          return error.message || 'Failed to access media devices. Please check your camera and microphone.';
      }
    }
  
    /**
     * Try with reduced constraints if initial request fails
     * @returns {Promise<Object>}
     */
    async initializeWithFallback() {
      console.log('🎥 Starting media initialization with fallback strategy');
      
      // Try 1: Full HD
      let result = await this.initializeLocalStream({ video: true, audio: true });
      if (result.success) {
        console.log('✅ Full HD stream acquired');
        return result;
      }
      
      console.log('⚠️ Full HD failed, trying 720p...');
      
      // Try 2: 720p
      result = await this.initializeLocalStream({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 }
        },
        audio: true
      });
      if (result.success) {
        console.log('✅ 720p stream acquired');
        return result;
      }
      
      console.log('⚠️ 720p failed, trying basic video...');
      
      // Try 3: Basic video
      result = await this.initializeLocalStream({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 }
        },
        audio: true
      });
      if (result.success) {
        console.log('✅ Basic video stream acquired');
        return result;
      }
      
      console.log('⚠️ Video failed, trying audio only...');
      
      // Try 4: Audio only
      result = await this.initializeLocalStream({ video: false, audio: true });
      if (result.success) {
        console.log('✅ Audio-only stream acquired');
        return result;
      }
      
      console.log('❌ All media initialization attempts failed');
      return result;
    }
  
    /**
     * Create peer connection with proper callbacks
     * @param {string} peerId - Remote peer ID
     * @param {Object} callbacks - Event callbacks
     * @returns {RTCPeerConnection}
     */
    createPeerConnection(peerId, callbacks = {}) {
      console.log('🔗 Creating peer connection for:', peerId);
      
      // Close existing connection if any
      if (this.peerConnections.has(peerId)) {
        console.log('🔗 Closing existing connection for:', peerId);
        this.removePeerConnection(peerId);
      }
  
      const peerConnection = new RTCPeerConnection(this.configuration);
      
      // Add local stream tracks to peer connection
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => {
          console.log(`➕ Adding ${track.kind} track to peer ${peerId}`, {
            id: track.id,
            label: track.label,
            enabled: track.enabled,
            readyState: track.readyState
          });
          peerConnection.addTrack(track, this.localStream);
        });
      } else {
        console.warn('⚠️ No local stream available when creating peer connection');
      }
  
      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          console.log(`🧊 ICE candidate for peer ${peerId}:`, event.candidate.type);
          if (callbacks.onIceCandidate) {
            callbacks.onIceCandidate(event.candidate);
          }
        } else {
          console.log(`✅ ICE gathering completed for peer ${peerId}`);
        }
      };
  
      // Handle remote tracks
      peerConnection.ontrack = (event) => {
        console.log(`📥 Received ${event.track.kind} track from peer ${peerId}`, {
          id: event.track.id,
          label: event.track.label,
          enabled: event.track.enabled,
          streams: event.streams.length
        });
        if (callbacks.onTrack) {
          callbacks.onTrack(event);
        }
      };
  
      // Handle connection state changes
      peerConnection.onconnectionstatechange = () => {
        console.log(`🔗 Peer ${peerId} connection state:`, peerConnection.connectionState);
        
        if (callbacks.onConnectionStateChange) {
          callbacks.onConnectionStateChange(peerConnection.connectionState);
        }
  
        if (peerConnection.connectionState === 'failed') {
          console.error(`❌ Connection to peer ${peerId} failed`);
        } else if (peerConnection.connectionState === 'disconnected') {
          console.warn(`⚠️ Disconnected from peer ${peerId}`);
        } else if (peerConnection.connectionState === 'connected') {
          console.log(`✅ Connected to peer ${peerId}`);
        }
  
        if (peerConnection.connectionState === 'closed') {
          this.removePeerConnection(peerId);
        }
      };
  
      // Handle ICE connection state changes
      peerConnection.oniceconnectionstatechange = () => {
        console.log(`🧊 Peer ${peerId} ICE connection state:`, peerConnection.iceConnectionState);
        
        if (callbacks.onIceConnectionStateChange) {
          callbacks.onIceConnectionStateChange(peerConnection.iceConnectionState);
        }
      };
  
      // Handle signaling state changes
      peerConnection.onsignalingstatechange = () => {
        console.log(`📡 Peer ${peerId} signaling state:`, peerConnection.signalingState);
      };
  
      // Handle negotiation needed
      peerConnection.onnegotiationneeded = async () => {
        console.log(`🔄 Negotiation needed for peer ${peerId}`);
        if (callbacks.onNegotiationNeeded) {
          callbacks.onNegotiationNeeded();
        }
      };
  
      this.peerConnections.set(peerId, peerConnection);
      console.log(`✅ Peer connection created for ${peerId}. Total connections: ${this.peerConnections.size}`);
      
      return peerConnection;
    }
  
    /**
     * Get peer connection
     * @param {string} peerId - Peer ID
     * @returns {RTCPeerConnection}
     */
    getPeerConnection(peerId) {
      return this.peerConnections.get(peerId);
    }
  
    /**
     * Remove peer connection
     * @param {string} peerId - Peer ID
     */
    removePeerConnection(peerId) {
      const peerConnection = this.peerConnections.get(peerId);
      if (peerConnection) {
        console.log(`🗑️ Removing peer connection for ${peerId}`);
        
        // Stop all transceivers
        if (peerConnection.getTransceivers) {
          peerConnection.getTransceivers().forEach(transceiver => {
            if (transceiver.stop) {
              transceiver.stop();
            }
          });
        }
  
        peerConnection.close();
        this.peerConnections.delete(peerId);
        console.log(`✅ Peer connection removed. Remaining: ${this.peerConnections.size}`);
      }
    }
  
    /**
     * Toggle video with proper state management
     * @returns {boolean} - New video state
     */
    toggleVideo() {
      if (this.localStream) {
        const videoTrack = this.localStream.getVideoTracks()[0];
        if (videoTrack) {
          videoTrack.enabled = !videoTrack.enabled;
          console.log(`📹 Video ${videoTrack.enabled ? 'ENABLED' : 'DISABLED'}`);
          return videoTrack.enabled;
        } else {
          console.warn('⚠️ No video track found in local stream');
        }
      } else {
        console.warn('⚠️ No local stream available');
      }
      return false;
    }
  
    /**
     * Toggle audio with proper state management
     * @returns {boolean} - New audio state
     */
    toggleAudio() {
      if (this.localStream) {
        const audioTrack = this.localStream.getAudioTracks()[0];
        if (audioTrack) {
          audioTrack.enabled = !audioTrack.enabled;
          console.log(`🎤 Audio ${audioTrack.enabled ? 'ENABLED' : 'DISABLED'}`);
          return audioTrack.enabled;
        } else {
          console.warn('⚠️ No audio track found in local stream');
        }
      } else {
        console.warn('⚠️ No local stream available');
      }
      return false;
    }
  
    /**
     * Stop all tracks in local stream
     */
    stopLocalStream() {
      if (this.localStream) {
        this.localStream.getTracks().forEach(track => {
          track.stop();
          console.log(`⏹️ Stopped ${track.kind} track:`, track.label);
        });
        this.localStream = null;
        console.log('✅ Local stream stopped');
      }
    }
  
    /**
     * Stop screen sharing stream
     */
    stopScreenStream() {
      if (this.screenStream) {
        this.screenStream.getTracks().forEach(track => {
          track.stop();
          console.log(`⏹️ Stopped screen share ${track.kind} track:`, track.label);
        });
        this.screenStream = null;
        console.log('✅ Screen stream stopped');
      }
    }
  
    /**
     * Close all connections
     */
    closeAllConnections() {
      console.log('🔒 Closing all peer connections...');
      this.peerConnections.forEach((pc, peerId) => {
        this.removePeerConnection(peerId);
      });
      this.peerConnections.clear();
      this.stopLocalStream();
      this.stopScreenStream();
      console.log('✅ All connections closed');
    }
  
    /**
     * Share screen with better error handling and options
     * @returns {Promise<Object>} - { success: boolean, stream?: MediaStream, error?: string }
     */
    async startScreenShare() {
      try {
        console.log('🖥️ Starting screen share...');
        
        // Check if getDisplayMedia is supported
        if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
          throw new Error('Screen sharing is not supported in your browser. Please use Chrome, Firefox, or Edge.');
        }
  
        // Stop existing screen stream if any
        if (this.screenStream) {
          console.log('🖥️ Stopping existing screen stream');
          this.stopScreenStream();
        }
  
        // Request screen share with options
        const displayMediaOptions = {
          video: {
            cursor: 'always',
            displaySurface: 'monitor',
            width: { ideal: 1920, max: 3840 },
            height: { ideal: 1080, max: 2160 },
            frameRate: { ideal: 30, max: 60 }
          },
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true
          }
        };
  
        console.log('🖥️ Requesting display media...');
        this.screenStream = await navigator.mediaDevices.getDisplayMedia(displayMediaOptions);
  
        const videoTracks = this.screenStream.getVideoTracks();
        const audioTracks = this.screenStream.getAudioTracks();
  
        console.log('✅ Screen share started successfully:');
        console.log('  🖥️ Video tracks:', videoTracks.length);
        if (videoTracks.length > 0) {
          const settings = videoTracks[0].getSettings();
          console.log('  🖥️ Screen settings:', {
            width: settings.width,
            height: settings.height,
            frameRate: settings.frameRate,
            displaySurface: settings.displaySurface
          });
        }
        console.log('  🔊 Audio tracks:', audioTracks.length);
  
        // Handle screen share stop (when user clicks "Stop Sharing" in browser)
        videoTracks[0].onended = () => {
          console.log('🖥️ Screen share ended by user via browser');
          this.stopScreenStream();
        };
  
        return { 
          success: true, 
          stream: this.screenStream,
          hasVideo: videoTracks.length > 0,
          hasAudio: audioTracks.length > 0
        };
      } catch (error) {
        console.error('❌ Error sharing screen:', error);
        console.error('  Error name:', error.name);
        console.error('  Error message:', error.message);
        
        let errorMessage = 'Failed to share screen';
        
        if (error.name === 'NotAllowedError') {
          errorMessage = 'Screen sharing permission denied. Please allow screen sharing and try again.';
        } else if (error.name === 'NotFoundError') {
          errorMessage = 'No screen available to share.';
        } else if (error.name === 'NotSupportedError') {
          errorMessage = 'Screen sharing is not supported in your browser.';
        } else if (error.name === 'AbortError') {
          errorMessage = 'Screen sharing was cancelled.';
        }
        
        return { 
          success: false, 
          error: errorMessage
        };
      }
    }
  
    /**
     * Replace video track with screen share or camera
     * @param {MediaStream} newStream - New stream (screen or camera)
     * @returns {Promise<boolean>} - Success status
     */
    async replaceVideoTrack(newStream) {
      const newVideoTrack = newStream.getVideoTracks()[0];
      
      if (!newVideoTrack) {
        console.error('❌ No video track found in stream');
        return false;
      }
      
      console.log('🔄 Replacing video track:', {
        label: newVideoTrack.label,
        id: newVideoTrack.id,
        enabled: newVideoTrack.enabled
      });
  
      const replacePromises = [];
      
      this.peerConnections.forEach((peerConnection, peerId) => {
        const sender = peerConnection.getSenders().find(s => s.track?.kind === 'video');
        if (sender) {
          console.log(`🔄 Replacing video track for peer ${peerId}`);
          replacePromises.push(
            sender.replaceTrack(newVideoTrack)
              .then(() => {
                console.log(`✅ Video track replaced successfully for peer ${peerId}`);
                return true;
              })
              .catch(error => {
                console.error(`❌ Error replacing track for peer ${peerId}:`, error);
                return false;
              })
          );
        } else {
          console.warn(`⚠️ No video sender found for peer ${peerId}`);
        }
      });
  
      await Promise.all(replacePromises);
      console.log('✅ Video track replacement completed for all peers');
      return true;
    }
  
    /**
     * Get current local stream
     * @returns {MediaStream}
     */
    getLocalStream() {
      return this.localStream;
    }
  
    /**
     * Get current screen stream
     * @returns {MediaStream}
     */
    getScreenStream() {
      return this.screenStream;
    }
  
    /**
     * Check if video is enabled
     * @returns {boolean}
     */
    isVideoEnabled() {
      if (this.localStream) {
        const videoTrack = this.localStream.getVideoTracks()[0];
        return videoTrack ? videoTrack.enabled : false;
      }
      return false;
    }
  
    /**
     * Check if audio is enabled
     * @returns {boolean}
     */
    isAudioEnabled() {
      if (this.localStream) {
        const audioTrack = this.localStream.getAudioTracks()[0];
        return audioTrack ? audioTrack.enabled : false;
      }
      return false;
    }
  
    /**
     * Get available media devices
     * @returns {Promise<Object>}
     */
    async getAvailableDevices() {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        
        const cameras = devices.filter(d => d.kind === 'videoinput');
        const microphones = devices.filter(d => d.kind === 'audioinput');
        const speakers = devices.filter(d => d.kind === 'audiooutput');
        
        console.log('📱 Available devices:', {
          cameras: cameras.length,
          microphones: microphones.length,
          speakers: speakers.length
        });
        
        return {
          cameras,
          microphones,
          speakers,
          hasCamera: cameras.length > 0,
          hasMicrophone: microphones.length > 0
        };
      } catch (error) {
        console.error('❌ Error enumerating devices:', error);
        return {
          cameras: [],
          microphones: [],
          speakers: [],
          hasCamera: false,
          hasMicrophone: false
        };
      }
    }
  
    /**
     * Get connection statistics
     * @param {string} peerId - Peer ID
     * @returns {Promise<Object>}
     */
    async getConnectionStats(peerId) {
      const peerConnection = this.peerConnections.get(peerId);
      if (!peerConnection) {
        return null;
      }
  
      try {
        const stats = await peerConnection.getStats();
        const result = {
          video: {},
          audio: {},
          connection: {}
        };
  
        stats.forEach(report => {
          if (report.type === 'inbound-rtp') {
            if (report.kind === 'video') {
              result.video.inbound = {
                packetsReceived: report.packetsReceived,
                bytesReceived: report.bytesReceived,
                packetsLost: report.packetsLost,
                framesPerSecond: report.framesPerSecond,
                framesDecoded: report.framesDecoded
              };
            } else if (report.kind === 'audio') {
              result.audio.inbound = {
                packetsReceived: report.packetsReceived,
                bytesReceived: report.bytesReceived,
                packetsLost: report.packetsLost
              };
            }
          } else if (report.type === 'outbound-rtp') {
            if (report.kind === 'video') {
              result.video.outbound = {
                packetsSent: report.packetsSent,
                bytesSent: report.bytesSent,
                framesPerSecond: report.framesPerSecond,
                framesEncoded: report.framesEncoded
              };
            } else if (report.kind === 'audio') {
              result.audio.outbound = {
                packetsSent: report.packetsSent,
                bytesSent: report.bytesSent
              };
            }
          } else if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            result.connection = {
              currentRoundTripTime: report.currentRoundTripTime,
              availableOutgoingBitrate: report.availableOutgoingBitrate
            };
          }
        });
  
        return result;
      } catch (error) {
        console.error('❌ Error getting connection stats:', error);
        return null;
      }
    }
  }
  
  export default new WebRTCService();